/**
 * =====================================================
 * AVA CLIENT - Login (UI Kit layout)
 * =====================================================
 * Layout completo del AVA Design System UI Kit:
 *   Panel izquierdo (hero): tagline "Plataforma de onboarding digital",
 *   título principal, párrafo descriptivo y card de marco regulatorio
 *   (UIF Res. 30/2017, BCRA A-6017, WCAG 2.1 AA).
 *   Panel derecho (480px): form con email + password + remember.
 *
 * Conservada toda la lógica de auth: rate limit handling, error de
 * credenciales, refresh de sesión.
 * =====================================================
 */
import { useState } from "react";
import { useSessionEndedNotice } from "@/hooks/useSessionEndedNotice";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertCircle,
  ArrowRight,
  Clock,
  Eye,
  EyeOff,
  Loader2,
  Lock,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { HeroPanel, LogoMark } from "@ava/shared/components/design-system";

const REGULATORY_CARDS = [
  { tag: "UIF Res. 30/2017", sub: "Prev. lavado" },
  { tag: "BCRA A-6017", sub: "Apertura digital" },
  { tag: "WCAG 2.1 AA", sub: "Accesibilidad" },
];

export default function Login() {
  const [, setLocation] = useLocation();
  // TALAPP-1862: explica por que se corto la sesion en vez de largar al
  // usuario en el login sin contexto. Null si entro por voluntad propia.
  const sessionEnded = useSessionEndedNotice();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user, loading: authLoading, refresh } = useAuth();

  if (user) {
    setLocation("/");
    return null;
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email || !password) {
      setError("Por favor completá email y contraseña");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
        credentials: "include",
      });
      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          const retryAfter = data.retryAfter || 60;
          setError(`Demasiados intentos. Intentá de nuevo en ${retryAfter} segundos.`);
        } else if (response.status === 401) {
          setError("Email o contraseña incorrectos");
        } else {
          setError(data.error || "Error de autenticación");
        }
        setIsLoading(false);
        return;
      }

      await refresh();
      setLocation("/");
    } catch (err: unknown) {
      void err;
      setError("Error de conexión. Por favor intentá de nuevo.");
      setIsLoading(false);
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
          <p className="text-sm text-muted-foreground">Verificando sesión...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* ===== LEFT PANEL — Hero del UI Kit ===== */}
      <HeroPanel className="hidden lg:flex lg:flex-1 min-h-screen p-12 xl:p-16 flex-col justify-between">
        <div className="w-full">
          {/* Header con logo */}
          <div className="flex items-center gap-3 relative z-10">
            <LogoMark tone="white" size={36} />
            <span className="font-bold text-[22px] tracking-tight text-white">AVA Onboarding</span>
          </div>

          {/* Middle — tagline + título + cards */}
          <div className="mt-16 max-w-xl">
            {/* Pill badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm text-[11px] font-semibold tracking-[0.08em] uppercase text-white">
              <span
                className="w-1.5 h-1.5 rounded-full bg-[#7CFFB2] animate-pulse"
                style={{ boxShadow: "0 0 0 3px rgba(124,255,178,0.25)" }}
              />
              Plataforma de onboarding digital
            </div>

            {/* Hero headline */}
            <h1 className="mt-5 text-[38px] font-bold leading-[1.12] tracking-[-0.02em] text-white">
              Onboarding digital de identidad, para cualquier industria.
            </h1>
            <p className="mt-4 text-base leading-relaxed text-white/85">
              Bancos, fintech, seguros, cripto, salud y sector público. Una sola plataforma para
              verificar personas humanas y jurídicas, de punta a punta.
            </p>

            {/* Regulatory card with glass-morphism */}
            <div className="mt-6 p-4 rounded-2xl bg-white/8 border border-white/20 backdrop-blur-md">
              <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.1em] uppercase text-white/75">
                <ShieldCheck className="w-3 h-3" />
                Marco regulatorio y de accesibilidad
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                {REGULATORY_CARDS.map((c) => (
                  <div
                    key={c.tag}
                    className="flex-1 min-w-[130px] bg-white/8 border border-white/20 rounded-xl px-3 py-2.5"
                  >
                    <div className="text-[13px] font-semibold tracking-tight text-white">{c.tag}</div>
                    <div className="text-[11px] mt-0.5 text-white/65">{c.sub}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-xs text-white/60">
          © 2026 AVA Compliance · Buenos Aires, Argentina
        </div>
      </HeroPanel>

      {/* ===== RIGHT PANEL — Form ===== */}
      <div className="w-full lg:w-[480px] flex flex-col justify-center p-6 sm:p-12 bg-white">
        {/* Mobile logo (solo visible en pantallas <lg) */}
        <div className="lg:hidden flex items-center gap-3 mb-8">
          <LogoMark size={40} />
          <div>
            <p className="font-bold text-lg">AVA Onboarding</p>
            <p className="text-xs text-muted-foreground">Portal del Solicitante</p>
          </div>
        </div>

        {/* Logo desktop dentro del form */}
        <div className="hidden lg:block">
          <LogoMark size={32} />
        </div>

        <h2 className="ava-h1 mt-5 text-foreground">Bienvenido</h2>
        <p className="ava-body-sm text-muted-foreground mt-1.5">
          Ingresá con tu cuenta para continuar tu trámite.
        </p>

        {sessionEnded && (
          <Alert role="status" aria-live="polite" className="mt-6">
            <Clock className="h-4 w-4" />
            <AlertDescription>
              {sessionEnded === "idle"
                ? "Tu sesión se cerró por inactividad. Ingresá de nuevo para continuar."
                : "Un administrador cerró tu sesión. Ingresá de nuevo para continuar."}
            </AlertDescription>
          </Alert>
        )}

        {error && (
          <Alert variant="destructive" role="alert" className="mt-6">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <form onSubmit={handleLogin} className="mt-7 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">Email</Label>
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                className="pl-10 h-11"
                autoComplete="email"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">Contraseña</Label>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                className="pl-10 pr-10 h-11"
                autoComplete="current-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex justify-end">
              <Link href="/forgot-password" className="text-[13px] text-primary hover:underline">
                ¿Olvidaste tu contraseña?
              </Link>
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-11 text-base font-medium gap-2"
            style={{ height: "var(--ava-control-h-lg, 44px)" }}
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Ingresando...
              </>
            ) : (
              <>
                Ingresar
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </form>

        <p className="mt-5 text-center text-sm text-muted-foreground">
          ¿No tenés cuenta?{" "}
          <Link href="/register" className="text-primary font-medium hover:underline">
            Crear cuenta
          </Link>
        </p>

        <p className="mt-6 text-center text-xs text-muted-foreground/80">
          Al ingresar aceptás los{" "}
          <a href="#" className="underline">Términos de Servicio</a> y la{" "}
          <a href="#" className="underline">Política de Privacidad</a>
        </p>
      </div>
    </div>
  );
}
